/**
 * Shared helpers for sales-order overpayment absorption.
 */

export const WRITE_OFF_TYPES = [
  "chargeOff",
  "sample-clearBalance",
  "promo-clearBalance",
];

// promotionCredit is already a credit instrument — minting a second spendable
// credit from it inflates brandStats promotionCredits.total and resets the
// source credit's expiration/promotion linkage.
export const NON_CREDITABLE_PAYMENT_TYPES = [
  ...WRITE_OFF_TYPES,
  "promotionCredit",
];

export function isCreditable(paymentType) {
  return !NON_CREDITABLE_PAYMENT_TYPES.includes(paymentType);
}

export function creditCapacity(payment) {
  return Number(
    parseFloat(
      Number(payment.amount) - Number(payment.creditsApplied ?? 0),
    ).toFixed(2),
  );
}

/**
 * LeafLink imports a reversal as a negative counter-entry flagged reversed and leaves the
 * original unflagged; native reversals flag the original and write no counter-entry.
 * Dropping every reversed row would strip the counter and overstate paid on imported orders.
 */
export function countsTowardPaid(payment) {
  return !payment.flags?.reversed || Number(payment.amount) < 0;
}

export function sumAppliedPayments(payments = []) {
  return Number(
    parseFloat(
      payments
        .filter(countsTowardPaid)
        .reduce(
          (sum, payment) =>
            sum +
            Number(payment.amount) -
            Number(payment.creditsApplied ?? 0),
          0,
        ),
    ).toFixed(2),
  );
}

/** Creditable, non-reversed payments (includes zero-capacity; needed for credit decrement). */
export function creditablePayments(payments = []) {
  return payments.filter(
    (payment) =>
      !payment.flags?.reversed && isCreditable(payment.paymentType),
  );
}

/** Creditable payments that can still absorb additional overpayment. */
export function absorbablePayments(payments = []) {
  return creditablePayments(payments).filter(
    (payment) => creditCapacity(payment) > 0,
  );
}

/** Total overpay the eligible payments can still absorb into credits. */
export function absorbableCapacity(payments = []) {
  return Number(
    parseFloat(
      absorbablePayments(payments).reduce(
        (sum, payment) => sum + creditCapacity(payment),
        0,
      ),
    ).toFixed(2),
  );
}

/**
 * Capacity reachable by topping up credits that already exist.
 * Overpay beyond this needs a brand-new credit, which requires a payableTo record.
 */
export function topUpCapacity(payments = []) {
  return Number(
    parseFloat(
      absorbablePayments(payments)
        .filter((payment) => payment.overpaymentCreditID)
        .reduce((sum, payment) => sum + creditCapacity(payment), 0),
    ).toFixed(2),
  );
}

/** Absorption order: newest payment first. */
export function sortNewestFirst(payments) {
  return [...payments].sort((a, b) => {
    const aTime = a.created?.seconds ?? 0;
    const bTime = b.created?.seconds ?? 0;
    return bTime - aTime;
  });
}

export function isFullyAbsorbed(runningBalance) {
  return Number(parseFloat(runningBalance).toFixed(2)) === 0;
}

/**
 * Overpay projected by a new order total.
 * Two paid bases exist: the payment docs, and the ledger (`balance.total - balance.due`)
 * that updateLineItems commits. They agree unless `due` is stale, so pass both and
 * block on whichever overpays more rather than letting a corrupt `due` slip through.
 */
export function computeOverpay({ newTotal, payments, balanceTotal, balanceDue }) {
  const paymentsApplied = sumAppliedPayments(payments);
  const rawLedgerApplied = Number(
    parseFloat(Number(balanceTotal) - Number(balanceDue)).toFixed(2),
  );
  const ledgerApplied = Number.isFinite(rawLedgerApplied)
    ? rawLedgerApplied
    : null;
  const effectiveApplied =
    ledgerApplied === null
      ? paymentsApplied
      : Math.max(paymentsApplied, ledgerApplied);
  const rawOverpay = Number(
    parseFloat(effectiveApplied - Number(newTotal)).toFixed(2),
  );
  return {
    paymentsApplied,
    ledgerApplied,
    overpay: rawOverpay >= 0.01 ? rawOverpay : 0,
  };
}

export function dueAfterRemoval({ balanceTotal, remainingPayments }) {
  return Number(
    parseFloat(
      Number(balanceTotal) - sumAppliedPayments(remainingPayments),
    ).toFixed(2),
  );
}

/** True when the linked overpayment credit has been spent past creditsApplied. */
export function isCreditSpent(credit, creditsApplied) {
  if (!credit) return true;
  const available = Number(credit.available);
  if (!Number.isFinite(available)) return true;
  return available < Number(creditsApplied ?? 0);
}
